import { EventEmitter } from '@root/Services/EventEmitter';
import { CostForecastQueryDatasourceFactory } from '@root/Services/Invoices/CostForecastService';
import { QueryDatasource, QueryDatasourceMetadata } from '@root/Services/Query/QueryDatasource';
import { SchemaService } from '@root/Services/QueryExpr';
import { inject, injectable } from 'tsyringe';
import { describeDatasource } from '../../Components/DatasourceDescriptor';
import { BaseDatasourcePresetsBuilder, IDatasourcePresetsBuilderFactory, INotificationPresetItem } from './BasePresets';

class CostForecastDatasourcePresetsBuilder extends BaseDatasourcePresetsBuilder {
    public constructor(queryDatasource: QueryDatasource, schemaSvc: SchemaService) {
        super(queryDatasource, schemaSvc);
    }

    public getPresets() {
        if (!this.schemaCtx.schemaSvc.types.length) {
            return [];
        }
        const { name, icon: Icon } = describeDatasource(this.baseQueryDatasource);
        return this.createDatasourcePresets(name, <Icon />, [this.createCustomForecastAlertPreset()], [this.createCustomForecastReportPreset()]);
    }

    // #region Alerts
    private createCustomForecastAlertPreset(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();

        const chartInput = this.createCustomChartBehavior(false);

        const behavior = this.createBehavior(({ data }) => {
            return this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                    QueryOptions: {
                        Parameters: {},
                        Filters: [],
                        DatasourceName: this.baseQueryDatasource.name,
                    },
                    Trigger: {
                        Threshold: {},
                    },
                },
                data
            );
        })
            .addForm(({ data }) => {
                const configChanged = new EventEmitter(data.ComponentConfig!);

                const chartSettings = chartInput(data, configChanged.emit);

                return [
                    chartSettings,
                    this.createSection(
                        'Alert Settings',
                        this.createSectionItem('Title and Description', ...this.createDescriptionInputs(data)),
                        this.createAlertConditionSection(data, configChanged)
                    ),
                ];
            })
            .addValidation(() => []);

        return this.createCustomAlertPreset(behavior);
    }
    // #endregion Alerts

    // #region Reports
    private createCustomForecastReportPreset(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();

        const chartInput = this.createCustomChartBehavior(false);

        const behavior = this.createBehavior(({ data }) => {
            return this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                    QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
                    Trigger: this.defaultSchedTrigger('Weekly'),
                },
                data
            );
        })
            .addForm(({ data }) => {
                const chartSettings = chartInput(data);

                return [
                    chartSettings,
                    this.createSection(
                        'Report Settings',
                        this.createSectionItem('Title and Description', ...this.createDescriptionInputs(data)),
                        this.createScheduleForm(data.Trigger?.Schedule!)
                    ),
                ];
            })
            .addValidation(() => []);

        return this.createCustomReportPreset(behavior);
    }
    // #endregion Reports
}
@injectable()
export class CostForecastDatasourcePresetsBuilderFactory implements IDatasourcePresetsBuilderFactory {
    public constructor(@inject(CostForecastQueryDatasourceFactory) private readonly fcDatasourceFactory: CostForecastQueryDatasourceFactory) {}

    public getSupportedDatasources(): QueryDatasourceMetadata[] {
        return [this.fcDatasourceFactory.getMetadata()];
    }

    public async get(_: string) {
        const queryDatasource = await this.fcDatasourceFactory.getDatasource();
        const types = await queryDatasource.schema.getSchema();
        const schemaSvc = SchemaService.create(types);
        return new CostForecastDatasourcePresetsBuilder(queryDatasource, schemaSvc);
    }
}
